/**
 * UpgradeModal
 *
 * Shown when the user hits the AI operation limit (HTTP 402 response).
 * Prompts the user to upgrade to the Starter plan.
 */

import React, { useState } from 'react';
import { Zap, Loader2 } from 'lucide-react';
import { createCheckoutSession } from '../api/billing';

interface UpgradeModalProps {
  onClose: () => void;
  stripeConfigured?: boolean;
}

export function UpgradeModal({ onClose, stripeConfigured = true }: UpgradeModalProps): React.ReactElement {
  const [upgrading, setUpgrading] = useState(false);

  const handleUpgrade = async () => {
    if (upgrading) return;

    if (!stripeConfigured) {
      window.location.href = '/settings';
      return;
    }

    setUpgrading(true);
    try {
      const { url } = await createCheckoutSession();
      window.location.href = url;
    } catch {
      setUpgrading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
    >
      <div className="bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-xl shadow-xl p-6 max-w-sm w-full mx-4 space-y-4 text-center">
        {/* Icon */}
        <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
          <Zap className="w-6 h-6 text-primary-500" />
        </div>

        {/* Title */}
        <h2
          id="upgrade-modal-title"
          className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark"
        >
          You've reached your AI limit
        </h2>

        {/* Body */}
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          You've used all 50 AI operations for this month on the free plan.
        </p>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Upgrade to Starter ($2/mo) for unlimited AI operations.
        </p>

        {/* Actions */}
        <div className="space-y-2 pt-2">
          <button
            onClick={handleUpgrade}
            disabled={upgrading}
            className="w-full px-4 py-2.5 text-sm font-semibold bg-primary-700 hover:bg-primary-800 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {upgrading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Upgrade now →'
            )}
          </button>

          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
